// TEAMS Website - Asset Path Utility
// Handles base path for assets in subdirectory deployment

// Get the base path from Vite's environment variable
const basePath = import.meta.env.BASE_URL || '/';

/**
 * Returns the full path for an asset considering the deployment base path
 * @param {string} path - The relative path from the public folder
 * @returns {string} - The full path including base path
 */
export const getAssetPath = (path) => {
  // Remove leading slash if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${basePath}${cleanPath}`;
};

/**
 * Returns the full path for an image in the main-slideshow folder
 * @param {string} imageName - The image filename
 * @returns {string} - The full path including base path
 */
export const getSlideshowImagePath = (imageName) => {
  return getAssetPath(`main-slideshow/${imageName}`);
};

/**
 * Returns the full path for an image in the events folder
 * @param {string} imagePath - The image path relative to events folder
 * @returns {string} - The full path including base path
 */
export const getEventsImagePath = (imagePath) => {
  return getAssetPath(`events/${imagePath}`);
};

/**
 * Returns the full path for an image in the ensemble folder
 * @param {string} imagePath - The image path relative to ensemble folder
 * @returns {string} - The full path including base path
 */
export const getEnsembleImagePath = (imagePath) => {
  return getAssetPath(`ensemble/${imagePath}`);
};

/**
 * Converts any existing absolute path to use the correct base path
 * @param {string} path - The original path (may start with /)
 * @returns {string} - The corrected path with base path
 */
export const convertAssetPath = (path) => {
  if (!path) return path;
  
  // If it's already a full URL, return as is
  if (path.startsWith('http')) return path;
  
  // If it starts with /, remove it and add base path
  if (path.startsWith('/')) {
    const cleanPath = path.slice(1);
    // URL encode spaces to handle folder names with spaces
    const encodedPath = cleanPath.replace(/ /g, '%20');
    return getAssetPath(encodedPath);
  }
  
  // If it doesn't start with /, just add base path
  const encodedPath = path.replace(/ /g, '%20');
  return getAssetPath(encodedPath);
};

export default getAssetPath;
